import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { SupplierInvoice } from '@/types/supplier';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { Button } from '@/components/ui/button';
import { fetchInvoiceItems } from '@/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ViewInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: SupplierInvoice | null;
}

export function ViewInvoiceDialog({ open, onOpenChange, invoice }: ViewInvoiceDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const [items, setItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // The invoice list endpoint never includes line items (only a single-invoice
  // fetch does), so `invoice.items` from the row data is always empty here —
  // fetch the real items for this invoice whenever the dialog opens.
  useEffect(() => {
    if (!open || !invoice) {
      setItems([]);
      return;
    }
    setLoadingItems(true);
    fetchInvoiceItems(invoice.id)
      .then(res => {
        const raw = res.data?.data || res.data || [];
        setItems(raw);
      })
      .catch(() => setItems([]))
      .finally(() => setLoadingItems(false));
  }, [open, invoice]);

  if (!invoice) return null;

  const formatCurrency = (amount: number) => `${currencySymbol}${amount.toFixed(2)}`;
  const formatDate = (date: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString();
  };

   const handleDownload = () => {
    if (!invoice) return;
    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text(`Supplier Invoice ${invoice.invoiceNumber}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(invoice.date)}`, 14, 26);
    doc.text(`Due: ${formatDate(invoice.dueDate)}`, 14, 32);
    doc.text(`Status: ${invoice.status}`, 14, 38);
    doc.text(`Amount: ${formatCurrency(invoice.amount)}`, 14, 44);
    doc.text(`Paid: ${formatCurrency(invoice.paidAmount)}`, 14, 50);
    doc.text(`Remaining: ${formatCurrency(invoice.amount - invoice.paidAmount)}`, 14, 56);

    const rows = items.map((item: any) => [
      item.item_name || item.name || '—',
      item.item_sku || item.sku || '—',
      item.barcode || '—',
      item.vat_enabled ? 'VAT' : '—',
      item.quantity,
      formatCurrency(parseFloat(item.unit_price)),
      formatCurrency(parseFloat(item.total_price)),
    ]);

    autoTable(doc, {
      startY: 64,
      head: [['Item', 'SKU', 'Barcode', 'VAT', 'Qty', 'Unit Price', 'Total']],
      body: rows,
      styles: { fontSize: 9 },
      foot: [
        [
          { content: 'Subtotal', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: formatCurrency(invoice.amount), styles: { fontStyle: 'bold' } }
        ]
      ],
    });

    doc.save(`Supplier-Invoice-${invoice.invoiceNumber}.pdf`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DialogTitle className="text-xl font-bold">
            Invoice Details - {invoice.invoiceNumber}
          </DialogTitle>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            Download PDF
          </Button>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Invoice Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoice Number:</span>
                <span className="font-semibold">{invoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span>{formatDate(invoice.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due Date:</span>
                <span>{formatDate(invoice.dueDate)}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <Badge variant={
                  invoice.status === 'paid' ? 'default' : 
                  invoice.status === 'partial' ? 'secondary' : 
                  'destructive'
                }>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-bold text-lg">{formatCurrency(invoice.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Paid Amount:</span>
                <span className="text-green-600">{formatCurrency(invoice.paidAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Remaining:</span>
                <span className={`font-semibold ${
                  (invoice.amount - invoice.paidAmount) > 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {formatCurrency(invoice.amount - invoice.paidAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>Items Purchased</span>
              {!loadingItems && (
                <Badge variant="outline">
                  {items.length} item{items.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </h3>

            {loadingItems ? (
              <div className="text-center py-8 text-gray-500">Loading items...</div>
            ) : items.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <Table className="min-w-[760px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Item Name</TableHead>
                      <TableHead className="font-semibold">SKU</TableHead>
                      <TableHead className="font-semibold">Barcode</TableHead>
                      <TableHead className="font-semibold text-center">VAT</TableHead>
                      <TableHead className="font-semibold text-center">Quantity</TableHead>
                      <TableHead className="font-semibold text-right">Unit Price</TableHead>
                      <TableHead className="font-semibold text-right">Total Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item: any, index: number) => (
                      <TableRow key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <TableCell className="font-medium">
                          {item.item_name || item.name || 'Unknown Item'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.item_sku || item.sku || '—'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {item.barcode || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.vat_enabled ? (
                            <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-100">VAT</Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {item.quantity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(parseFloat(item.unit_price))}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(parseFloat(item.total_price))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                
                {/* Summary Row */}
                <div className="bg-gray-100 px-4 py-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">
                        Total Items: {items.reduce((sum: number, item: any) => sum + parseInt(item.quantity), 0)}
                      </span>
                      <span className="text-lg font-bold">
                        Subtotal: {formatCurrency(invoice.amount)}
                      </span>
                    </div>
                  </div>
                </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 text-lg">No items recorded for this invoice</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
